
import React, { useState, useEffect, useCallback } from 'react';
import { userApiClient } from '../../api/HttpClient';
import BedsTable from './BedsTable';
import RentPaymentsTab from './RentPaymentsTab';
import CreateRoomDialog from './CreateRoomDialog';
import ExpenseTracker from '../ExpenseTracker';
import TicketDashboard from './TicketDashboard';
import RatingsAndReviews from './RatingsAndReviews';
import MonthlySummaryTab from './MonthlySummaryTab';
import {
  CircularProgress
} from '@mui/material';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Eye, Link2, Copy, Check, Download, QrCode
} from "lucide-react";
import QRCode from 'qrcode';
import { Button } from '../ui/button';
import { cn } from "../../lib/utils";
import * as hostService from "../../api/services/hostService";
import * as pgTenantService from "../../api/services/pgTenantService";
import { ICreateRoomRequest } from "../../app/model/hostModel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

interface LongTermVacancyProps {
  propertyId: string;
  propertyName?: string;
}

interface Room {
  id: string;
  room_number: string;
  capacity: number;
  occupied_beds: number;
  vacant_beds: number;
}

// Banner shown in Tickets and Ratings tabs — copy link or download print-ready QR
const TenantFormLinkBanner: React.FC<{ propertyId: string; type: 'ticket' | 'rating'; propertyName?: string }> = ({ propertyId, type, propertyName = '' }) => {
  const [copied, setCopied]       = useState(false);
  const [downloading, setDownloading] = useState(false);

  const base  = window.location.origin;
  const link  = type === 'ticket'
    ? `${base}/submit/ticket?p=${propertyId}`
    : `${base}/submit/rating?p=${propertyId}`;

  const isTicket   = type === 'ticket';
  const label      = isTicket ? 'Ticket' : 'Rating';
  const emoji      = isTicket ? '🎫' : '⭐';
  const title      = isTicket ? 'Have an issue? Scan to raise a ticket' : 'Rate your stay — Scan to give feedback';
  const accent     = isTicket ? '#f97316' : '#eab308';   // orange-500 / yellow-500
  const accentBg   = isTicket ? '#fff7ed' : '#fefce8';   // orange-50  / yellow-50
  const accentBdr  = isTicket ? '#fed7aa' : '#fef08a';   // orange-200 / yellow-200
  const accentText = isTicket ? 'text-orange-800' : 'text-yellow-800';
  const accentMono = isTicket ? 'text-orange-600' : 'text-yellow-600';
  const btnClass   = isTicket
    ? 'bg-orange-500 hover:bg-orange-600 text-white'
    : 'bg-yellow-500 hover:bg-yellow-600 text-white';

  const handleCopy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Generates a print-ready PNG: PG name + action text → QR → socialbunkr.in promo
  const handleDownloadQR = async () => {
    setDownloading(true);
    try {
      const width     = 420;
      const padding   = 28;
      const qrSize    = width - padding * 2;
      const headerH   = propertyName ? 72 : 44;   // PG name row + action text
      const footerH   = 64;
      const totalH    = headerH + qrSize + footerH;

      // 1. Generate raw QR
      const qrDataUrl = await QRCode.toDataURL(link, {
        width: qrSize,
        margin: 1,
        color: { dark: '#1f2937', light: '#ffffff' },
      });

      // 2. Compose canvas
      const canvas  = document.createElement('canvas');
      canvas.width  = width;
      canvas.height = totalH;
      const ctx     = canvas.getContext('2d')!;

      // Background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, totalH);

      // Card border
      ctx.strokeStyle = accentBdr;
      ctx.lineWidth   = 2;
      ctx.roundRect(4, 4, width - 8, totalH - 8, 16);
      ctx.stroke();

      // ── Header ───────────────────────────────────────────────────
      ctx.textAlign = 'center';

      if (propertyName) {
        ctx.fillStyle = '#1a3d32';
        ctx.font      = 'bold 18px sans-serif';
        ctx.fillText(propertyName, width / 2, 32);
      }

      ctx.fillStyle = accent;
      ctx.font      = `bold 13px sans-serif`;
      ctx.fillText(title, width / 2, propertyName ? 56 : 28);

      // ── QR image ─────────────────────────────────────────────────
      const img = new Image();
      img.src   = qrDataUrl;
      await new Promise<void>((res) => { img.onload = () => res(); });
      ctx.drawImage(img, padding, headerH, qrSize, qrSize);

      // ── socialbunkr.in footer ─────────────────────────────────────
      const footerY = headerH + qrSize;

      // Footer background band
      ctx.fillStyle = '#f0f7f4';
      ctx.fillRect(0, footerY, width, footerH);

      // Divider line
      ctx.strokeStyle = '#c8ddd7';
      ctx.lineWidth   = 1;
      ctx.beginPath();
      ctx.moveTo(0, footerY);
      ctx.lineTo(width, footerY);
      ctx.stroke();

      ctx.textAlign = 'center';
      ctx.fillStyle = '#1a3d32';
      ctx.font      = 'bold 15px sans-serif';
      ctx.fillText('socialbunkr.in', width / 2, footerY + 24);

      ctx.fillStyle = '#4a7a6a';
      ctx.font      = '11px sans-serif';
      ctx.fillText("Bharat's Affordable Stay Booking Platform", width / 2, footerY + 44);

      // Download
      const a    = document.createElement('a');
      a.href     = canvas.toDataURL('image/png');
      a.download = `socialbunkr-${type}-qr.png`;
      a.click();
    } catch (err) {
      console.error('QR generation failed', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="rounded-xl border p-4"
      style={{ background: accentBg, borderColor: accentBdr }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold mb-1 ${accentText}`}>
            {emoji} {label} Link for Tenants
          </p>
          <p className={`text-xs mb-3 truncate font-mono ${accentMono}`}>{link}</p>
          <div className="flex flex-wrap gap-2">
            {/* Copy link */}
            <button
              onClick={handleCopy}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${btnClass}`}
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? 'Copied!' : 'Copy Link'}
            </button>

            {/* Download QR */}
            <button
              onClick={handleDownloadQR}
              disabled={downloading}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium bg-gray-700 hover:bg-gray-800 text-white transition-colors disabled:opacity-50"
            >
              {downloading
                ? <><QrCode className="h-3 w-3 animate-pulse" /> Generating...</>
                : <><Download className="h-3 w-3" /> Download QR</>}
            </button>
          </div>

          <p className="text-xs text-gray-400 mt-2">
            Print this QR code and paste it at your reception or notice board.
          </p>
        </div>
        <Link2 className={`h-5 w-5 shrink-0 mt-0.5 ${accentMono}`} />
      </div>
    </div>
  );
};

const LongTermVacancy: React.FC<LongTermVacancyProps> = ({ propertyId, propertyName = '' }) => {
  const [tenantSubTab, setTenantSubTab] = useState<"occupancy" | "rent" | "expense" | "tickets" | "ratings" | "summary" | "vacate-notices" | "electricity">("occupancy");
  const [vacateNotices, setVacateNotices] = useState<any[]>([]);
  const [vacateNoticesLoading, setVacateNoticesLoading] = useState(false);
  // Electricity bills state
  const now = new Date();
  const [billMonth, setBillMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [electricityBills, setElectricityBills] = useState<any[]>([]);
  const [allTenants, setAllTenants] = useState<any[]>([]);
  const [electricityLoading, setElectricityLoading] = useState(false);
  const [billDialog, setBillDialog] = useState<{ tenantId: string; tenantName: string; bill: any | null } | null>(null);
  const [billForm, setBillForm] = useState({ amount: '', units_consumed: '', notes: '', is_paid: false });
  const [billSaving, setBillSaving] = useState(false);
  const [billError, setBillError] = useState<string | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showCreateRoomDialog, setShowCreateRoomDialog] = useState(false);
  const [createRoomForm, setCreateRoomForm] = useState<ICreateRoomRequest>({ room_number: "", capacity: 0, description: "" });
  const [customDescription, setCustomDescription] = useState("");
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [selectedRoomNumber, setSelectedRoomNumber] = useState<string | null>(null);
  const [editRoom, setEditRoom] = useState<{ id: string; room_number: string; capacity: number } | null>(null);
  const [editCapacity, setEditCapacity] = useState('');
  const [editRoomName, setEditRoomName] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);


  useEffect(() => {
    if (propertyId) {
      fetchRooms(propertyId);
    }
  }, [propertyId]);

  useEffect(() => {
    if (tenantSubTab === 'vacate-notices' && propertyId) {
      fetchVacateNotices();
    }
  }, [tenantSubTab, propertyId]);

  useEffect(() => {
    if (tenantSubTab === 'electricity' && propertyId) {
      fetchElectricityData();
    }
  }, [tenantSubTab, propertyId, billMonth]);

  const fetchVacateNotices = async () => {
    setVacateNoticesLoading(true);
    try {
      const client = await userApiClient;
      const data = await client.get<any[]>(`/api/pg_tenant/property/${propertyId}/vacate-notices/`);
      setVacateNotices(data);
    } catch (e) {
      console.error('Failed to fetch vacate notices', e);
    } finally {
      setVacateNoticesLoading(false);
    }
  };

  const fetchElectricityData = async () => {
    setElectricityLoading(true);
    try {
      const client = await userApiClient;
      const [bills, paymentsResp] = await Promise.all([
        client.get<any[]>(`/api/pg_tenant/property/${propertyId}/electricity-bills/?month=${billMonth}`),
        client.get<any>(`/api/pg_tenant/property/${propertyId}/payments/`),
      ]);
      setElectricityBills(Array.isArray(bills) ? bills : []);
      // Payments API returns { payments: [...] }
      const paymentList: any[] = paymentsResp?.payments ?? (Array.isArray(paymentsResp) ? paymentsResp : []);
      const seen = new Set<string>();
      const tenants: any[] = [];
      for (const p of paymentList) {
        if (!seen.has(p.tenant_id)) {
          seen.add(p.tenant_id);
          tenants.push({ id: p.tenant_id, name: p.tenant_name, phone: p.tenant_phone, room_number: p.room_number });
        }
      }
      setAllTenants(tenants);
    } catch (e) {
      console.error('Failed to fetch electricity data', e);
    } finally {
      setElectricityLoading(false);
    }
  };

  const openBillDialog = (tenantId: string, tenantName: string, existingBill: any | null) => {
    setBillDialog({ tenantId, tenantName, bill: existingBill });
    setBillForm({
      amount: existingBill ? String(existingBill.amount) : '',
      units_consumed: existingBill?.units_consumed != null ? String(existingBill.units_consumed) : '',
      notes: existingBill?.notes || '',
      is_paid: existingBill?.is_paid || false,
    });
    setBillError(null);
  };

  const saveBill = async () => {
    if (!billDialog || !billForm.amount || isNaN(Number(billForm.amount))) {
      setBillError('Please enter a valid amount.');
      return;
    }
    setBillSaving(true);
    setBillError(null);
    try {
      const client = await userApiClient;
      const existingBillId = billDialog.bill?.id;
      if (existingBillId) {
        // Edit: use PATCH which properly handles is_paid + paid_at
        const patchBody: any = {
          amount: Number(billForm.amount),
          is_paid: billForm.is_paid,
          notes: billForm.notes,
        };
        if (billForm.units_consumed) patchBody.units_consumed = Number(billForm.units_consumed);
        else patchBody.units_consumed = null;
        await client.patch(`/api/pg_tenant/electricity-bills/${existingBillId}/`, patchBody);
      } else {
        // Create: POST with update_or_create
        const body: any = {
          tenant_id: billDialog.tenantId,
          month: billMonth,
          amount: Number(billForm.amount),
          is_paid: billForm.is_paid,
          notes: billForm.notes,
        };
        if (billForm.units_consumed) body.units_consumed = Number(billForm.units_consumed);
        await client.post(`/api/pg_tenant/property/${propertyId}/electricity-bills/`, body);
      }
      setBillDialog(null);
      fetchElectricityData();
    } catch (e: any) {
      setBillError(e.message || 'Failed to save bill.');
    } finally {
      setBillSaving(false);
    }
  };

  const deleteBill = async (billId: string) => {
    if (!window.confirm('Delete this electricity bill?')) return;
    try {
      const client = await userApiClient;
      await client.delete(`/api/pg_tenant/electricity-bills/${billId}/`);
      fetchElectricityData();
    } catch (e) {
      console.error('Failed to delete bill', e);
    }
  };

  const fetchRooms = async (id: string) => {
    setLoading(true);
    try {
      const client = await userApiClient;
      const response = await client.get<Room[]>(`/api/pg_tenant/longterm/properties/${id}/rooms/`);
      setRooms(response);
    } catch (error) {
      console.error("Failed to fetch rooms:", error);
    } finally {
      setLoading(false);
    }
  };

  const ROOM_DESCRIPTION_OPTIONS = [
    "Spacious room with a comfortable bed, wardrobe, and study desk — ideal for professionals.",
    "Well-ventilated room with attached bathroom, WiFi, and daily housekeeping.",
    "Cozy room with ample storage, ceiling fan, and 24/7 security.",
    "Bright and airy room with individual locker, WiFi, and common kitchen access.",
    "Affordable room with bunk beds, locker, and access to shared bathroom facilities.",
    "Fully furnished room with AC, wardrobe, and study table — perfect for students.",
    "Clean and well-maintained room with CCTV security and common amenities.",
    "Custom",
  ];

  const handleCreateRoom = async () => {
    const finalDescription = createRoomForm.description === "Custom" ? customDescription.trim() : createRoomForm.description;
    try {
      await pgTenantService.createRoom({ ...createRoomForm, capacity: parseInt(createRoomForm.capacity as any), property_id: propertyId, description: finalDescription });
      fetchRooms(propertyId);
      setShowCreateRoomDialog(false);
      setCreateRoomForm({ room_number: "", capacity: 0, description: "" });
      setCustomDescription("");
    } catch (error) {
      console.error("Failed to create room:", error);
    }
  };

  const handleSaveEdit = async () => {
    if (!editRoom) return;
    const body: Record<string, any> = {};
    if (editRoomName.trim() && editRoomName.trim() !== editRoom.room_number)
      body.room_number = editRoomName.trim();
    if (editCapacity && Number(editCapacity) >= 1)
      body.capacity = Number(editCapacity);
    if (Object.keys(body).length === 0) { setEditError('Change room name or bed count to save.'); return; }
    setEditLoading(true);
    setEditError(null);
    try {
      const client = await userApiClient;
      await client.patch(`/api/pg_tenant/longterm/rooms/${editRoom.id}/edit/`, body);
      setEditRoom(null);
      fetchRooms(propertyId);
    } catch (e: any) {
      setEditError(e.message || 'Failed to update room.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleViewBeds = (roomId: string, roomNumber: string) => {
    setSelectedRoomId(roomId);
    setSelectedRoomNumber(roomNumber);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <CircularProgress />
      </div>
    );
  }

  if (selectedRoomId && selectedRoomNumber) {
    return <BedsTable roomId={selectedRoomId} roomNumber={selectedRoomNumber} propertyId={propertyId} onClose={() => setSelectedRoomId(null)} />;
  }

  return (
    <div>
      {/* Tenant Sub Tabs */}
      <div className="border-b border-border mb-6">
        <div className="flex flex-wrap gap-4">
          {[
            { key: "occupancy", label: "OCCUPANCY OVERVIEW" },
            { key: "rent", label: "RENT PAYMENTS" },
            { key: "expense", label: "EXPENSE TRACKER" },
            { key: "tickets", label: "TICKETS" },
            { key: "ratings", label: "RATINGS & REVIEWS" },
            { key: "summary", label: "MONTHLY SUMMARY" },
            { key: "vacate-notices", label: "VACATE NOTICES" },
            { key: "electricity", label: "ELECTRICITY BILLS" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setTenantSubTab(tab.key as typeof tenantSubTab)}
              className={cn(
                "pb-3 px-1 font-medium transition-colors relative text-sm",
                tenantSubTab === tab.key
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {tab.label}
              {tenantSubTab === tab.key && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Occupancy Overview Tab */}
      {tenantSubTab === "occupancy" && (
        <div>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-primary rounded-xl p-4">
              <p className="text-primary-foreground/80 text-sm">Total Rooms</p>
              <p className="text-3xl font-bold text-primary-foreground">{rooms.length}</p>
            </div>
            <div className="bg-primary rounded-xl p-4">
              <p className="text-primary-foreground/80 text-sm">Total Beds</p>
              <p className="text-3xl font-bold text-primary-foreground">
                {rooms.reduce((acc, room) => acc + (room.capacity || 0), 0)}
              </p>
            </div>
            <div className="bg-secondary rounded-xl p-4">
              <p className="text-secondary-foreground/80 text-sm">Occupied Beds</p>
              <p className="text-3xl font-bold text-secondary-foreground">
                {rooms.reduce((acc, room) => acc + (room.occupied_beds || 0), 0)}
              </p>
            </div>
            <div className="bg-secondary rounded-xl p-4">
              <p className="text-secondary-foreground/80 text-sm">Vacant Beds</p>
              <p className="text-3xl font-bold text-secondary-foreground">
                {rooms.reduce((acc, room) => acc + ((room.capacity || 0) - (room.occupied_beds || 0)), 0)}
              </p>
            </div>
            <div className="bg-secondary rounded-xl p-4">
              <p className="text-secondary-foreground/80 text-sm">Occupancy Rate</p>
              <p className="text-3xl font-bold text-secondary-foreground">
                {rooms.reduce((acc, room) => acc + (room.capacity || 0), 0) > 0
                  ? ((rooms.reduce((acc, room) => acc + (room.occupied_beds || 0), 0) / rooms.reduce((acc, room) => acc + (room.capacity || 0), 0)) * 100).toFixed(2)
                  : 0}%
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mb-6">
            <Button
              onClick={() => setShowCreateRoomDialog(true)}
              className="bg-primary hover:bg-primary/90"
            >
              ADD ROOMS
            </Button>
          </div>

          {/* Rooms Table */}
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-4">Rooms</h3>
            <div className="border border-border rounded-xl overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary hover:bg-primary">
                    <TableHead className="text-primary-foreground font-semibold">Room Number</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Capacity</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Occupied Beds</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Vacant Beds</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rooms.map((room) => (
                    <TableRow key={room.id} className="hover:bg-muted/50">
                      <TableCell className="font-medium">{room.room_number}</TableCell>
                      <TableCell>{room.capacity}</TableCell>
                      <TableCell>{room.occupied_beds}</TableCell>
                      <TableCell>{(room.capacity || 0) - (room.occupied_beds || 0)}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleViewBeds(room.id, room.room_number)}>
                            <Eye className="h-4 w-4 mr-1" />
                            VIEW BEDS
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => {
                            setEditRoom({ id: room.id, room_number: room.room_number, capacity: room.capacity });
                            setEditCapacity(String(room.capacity));
                            setEditRoomName(room.room_number);
                            setEditError(null);
                          }}>
                            Edit Room
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      )}

      {tenantSubTab === "rent" && <RentPaymentsTab propertyId={propertyId} />}
      {tenantSubTab === "expense" && <ExpenseTracker propertyId={propertyId} />}
      {tenantSubTab === "tickets" && (
        <div className="space-y-4">
          <TenantFormLinkBanner propertyId={propertyId} type="ticket" propertyName={propertyName} />
          <TicketDashboard propertyId={propertyId} />
        </div>
      )}
      {tenantSubTab === "ratings" && (
        <div className="space-y-4">
          <TenantFormLinkBanner propertyId={propertyId} type="rating" propertyName={propertyName} />
          <RatingsAndReviews propertyId={propertyId} />
        </div>
      )}
      {tenantSubTab === "summary" && <MonthlySummaryTab propertyId={propertyId} />}

      {tenantSubTab === "vacate-notices" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-[#276859] text-lg">Vacate Notices</h3>
              <p className="text-xs text-gray-500 mt-0.5">Tenants who have submitted a vacate notice</p>
            </div>
            <button onClick={fetchVacateNotices} className="text-xs text-[#276859] underline">Refresh</button>
          </div>
          {vacateNoticesLoading ? (
            <div className="flex justify-center py-12"><CircularProgress size={28} sx={{ color: '#276859' }} /></div>
          ) : vacateNotices.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-4xl mb-3">🚪</p>
              <p className="font-medium text-gray-500">No vacate notices yet</p>
              <p className="text-xs mt-1">Tenant submitted vacate notices will appear here.</p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {vacateNotices.map((n: any) => (
                <div key={n.id} className="bg-orange-50 border border-orange-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-bold text-gray-800 text-sm">{n.tenant_name}</p>
                      <p className="text-xs text-gray-500">{n.tenant_phone}</p>
                    </div>
                    <span className="text-xs bg-orange-100 text-orange-700 font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">Pending</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                    <div>
                      <p className="text-gray-400">Planned Vacate</p>
                      <p className="font-semibold text-gray-700">{new Date(n.planned_vacate_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Moving to PG</p>
                      <p className="font-semibold text-gray-700">{n.moving_to_pg ? 'Yes' : 'No'}</p>
                    </div>
                    {n.next_area && (
                      <div>
                        <p className="text-gray-400">Next Area</p>
                        <p className="font-semibold text-gray-700">{n.next_area}</p>
                      </div>
                    )}
                    {n.next_city && (
                      <div>
                        <p className="text-gray-400">Next City</p>
                        <p className="font-semibold text-gray-700">{n.next_city}</p>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-400">Submitted on {new Date(n.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tenantSubTab === "electricity" && (
        <div className="space-y-5">
          {/* Header + month picker */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-[#276859] text-lg">Electricity Bills</h3>
              <p className="text-xs text-gray-500 mt-0.5">Monthly electricity bills for each tenant</p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-500">Month:</label>
              <input
                type="month"
                value={billMonth}
                onChange={e => setBillMonth(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#276859]/30 bg-white"
              />
              <button onClick={fetchElectricityData} className="text-xs text-[#276859] underline">Refresh</button>
            </div>
          </div>

          {electricityLoading ? (
            <div className="flex justify-center py-12"><CircularProgress size={28} sx={{ color: '#276859' }} /></div>
          ) : allTenants.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-4xl mb-3">⚡</p>
              <p className="font-medium text-gray-500">No active tenants found</p>
              <p className="text-xs mt-1">Assign tenants to beds to manage electricity bills.</p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {allTenants.map(tenant => {
                const bill = electricityBills.find(b => b.tenant_id === tenant.id) || null;
                return (
                  <div key={tenant.id} className={`rounded-xl p-4 border ${bill ? (bill.is_paid ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200') : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div>
                        <p className="font-bold text-gray-800 text-sm">{tenant.name}</p>
                        <p className="text-xs text-gray-500">{tenant.room_number && `Room ${tenant.room_number} · `}{tenant.phone}</p>
                      </div>
                      {bill ? (
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${bill.is_paid ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                          {bill.is_paid ? 'Paid' : 'Pending'}
                        </span>
                      ) : (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap bg-gray-200 text-gray-500">No Bill</span>
                      )}
                    </div>

                    {bill ? (
                      <div className="mb-3 space-y-1">
                        <p className={`text-xl font-bold ${bill.is_paid ? 'text-green-700' : 'text-gray-800'}`}>
                          ₹{Number(bill.amount).toLocaleString('en-IN')}
                        </p>
                        {bill.units_consumed != null && (
                          <p className="text-xs text-gray-500">{bill.units_consumed} units</p>
                        )}
                        {bill.notes && <p className="text-xs text-gray-500 truncate">{bill.notes}</p>}
                        {bill.is_paid && bill.paid_at && (
                          <p className="text-xs text-green-600">Paid on {new Date(bill.paid_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 mb-3">No bill added for this month.</p>
                    )}

                    <div className="flex gap-2">
                      <button
                        onClick={() => openBillDialog(tenant.id, tenant.name, bill)}
                        className="flex-1 text-xs font-semibold bg-[#276859] text-white rounded-lg py-1.5 hover:bg-[#276859]/90 transition-colors"
                      >
                        {bill ? 'Edit Bill' : '+ Add Bill'}
                      </button>
                      {bill && (
                        <button
                          onClick={() => deleteBill(bill.id)}
                          className="text-xs font-semibold border border-red-300 text-red-500 rounded-lg px-3 py-1.5 hover:bg-red-50 transition-colors"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Bill Add/Edit Dialog */}
      {billDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
            <div className="bg-[#276859] px-5 py-4">
              <h2 className="text-white font-bold text-lg">{billDialog.bill ? 'Edit' : 'Add'} Electricity Bill</h2>
              <p className="text-white/70 text-sm">{billDialog.tenantName} · {billMonth}</p>
            </div>
            <div className="p-5 space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600">Amount (INR) *</label>
                <input
                  type="number"
                  min="0"
                  value={billForm.amount}
                  onChange={e => setBillForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="e.g. 450"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#276859]/30"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600">Units Consumed (optional)</label>
                <input
                  type="number"
                  min="0"
                  value={billForm.units_consumed}
                  onChange={e => setBillForm(f => ({ ...f, units_consumed: e.target.value }))}
                  placeholder="e.g. 45"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#276859]/30"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-gray-600">Notes (optional)</label>
                <input
                  type="text"
                  value={billForm.notes}
                  onChange={e => setBillForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="e.g. Shared meter reading"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#276859]/30"
                />
              </div>
              <div className="flex items-center justify-between py-1">
                <label className="text-sm font-medium text-gray-700">Mark as Paid</label>
                <button
                  type="button"
                  onClick={() => setBillForm(f => ({ ...f, is_paid: !f.is_paid }))}
                  className={`relative w-11 h-6 rounded-full transition-colors ${billForm.is_paid ? 'bg-[#276859]' : 'bg-gray-300'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${billForm.is_paid ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
              {billError && <p className="text-sm text-red-500">{billError}</p>}
            </div>
            <div className="px-5 pb-5 flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setBillDialog(null)} disabled={billSaving}>Cancel</Button>
              <Button
                className="bg-[#276859] hover:bg-[#276859]/90 text-white"
                onClick={saveBill}
                disabled={billSaving}
              >
                {billSaving ? 'Saving...' : 'Save Bill'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Rooms Dialog */}
      <CreateRoomDialog
        open={showCreateRoomDialog}
        onClose={() => setShowCreateRoomDialog(false)}
        propertyId={propertyId}
        propertyName={propertyName}
        onRoomCreated={() => fetchRooms(propertyId)}
      />

      {/* Edit Room Beds Dialog */}
      {editRoom && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
            <div className="bg-primary px-5 py-4">
              <h2 className="text-primary-foreground font-bold text-lg">Edit Room {editRoom.room_number}</h2>
              <p className="text-primary-foreground/70 text-sm">Rename room or change bed count</p>
            </div>
            <div className="p-5 space-y-4">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Room Name / Number</Label>
                <Input value={editRoomName}
                  onChange={e => setEditRoomName(e.target.value)}
                  placeholder="e.g. 501, G01" className="bg-background" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Number of Beds</Label>
                <Input type="number" min={1} value={editCapacity}
                  onChange={e => setEditCapacity(e.target.value)} className="bg-background" />
                <p className="text-xs text-muted-foreground">Current: {editRoom.capacity} beds. Occupied beds cannot be removed.</p>
              </div>
              {editError && <p className="text-sm text-red-500">{editError}</p>}
            </div>
            <div className="px-5 pb-5 flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setEditRoom(null)} disabled={editLoading}>Cancel</Button>
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground"
                onClick={handleSaveEdit} disabled={editLoading}>
                {editLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default LongTermVacancy;
